import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function PermitLicenseChecklistPage() {
  const generalRequirements = [
    {
      title: 'Business License',
      authority: 'City of El Paso',
      description: 'Required for all businesses operating within city limits. Renewed annually.',
      cost: '$50-$200 depending on business type',
      timeline: '2-4 weeks',
    },
    {
      title: 'Certificate of Occupancy',
      authority: 'City of El Paso Planning & Inspections',
      description: 'Certifies that your business location complies with building codes and zoning regulations.',
      cost: 'Varies by property size',
      timeline: '4-6 weeks',
    },
    {
      title: 'Sales Tax Permit',
      authority: 'Texas Comptroller',
      description: 'Required if you sell tangible goods or taxable services in Texas.',
      cost: 'Free',
      timeline: 'Immediate (online)',
    },
    {
      title: 'Employer Identification Number (EIN)',
      authority: 'IRS',
      description: 'Federal tax ID required for businesses with employees or certain business structures.',
      cost: 'Free',
      timeline: 'Immediate (online)',
    },
  ];

  const industrySpecific = [
    {
      category: 'Food & Beverage',
      icon: 'ri-restaurant-line',
      permits: [
        'Food Service Permit (El Paso Health Department)',
        'Food Handler Certification (all employees)',
        'Alcohol Beverage License (TABC - if serving alcohol)',
        'Health Inspection Certificate',
        'Fire Safety Inspection',
      ],
    },
    {
      category: 'Retail',
      icon: 'ri-store-line',
      permits: [
        'Retail Sales Permit',
        'Sign Permit (if installing signage)',
        'Fire Safety Inspection',
        'Building Permit (for renovations)',
      ],
    },
    {
      category: 'Construction & Contracting',
      icon: 'ri-hammer-line',
      permits: [
        'Contractor License (Texas Department of Licensing)',
        'Building Permits (per project)',
        'Electrical/Plumbing License (if applicable)',
        'Workers Compensation Insurance',
        'Surety Bond',
      ],
    },
    {
      category: 'Healthcare',
      icon: 'ri-hospital-line',
      permits: [
        'Professional License (Texas Medical Board)',
        'DEA Registration (if prescribing)',
        'HIPAA Compliance Certification',
        'Medical Waste Disposal Permit',
        'Facility License (Texas HHS)',
      ],
    },
    {
      category: 'Personal Services',
      icon: 'ri-scissors-line',
      permits: [
        'Cosmetology/Barber License (TDLR)',
        'Facility License',
        'Health Inspection',
        'Fire Safety Inspection',
      ],
    },
    {
      category: 'Home-Based Business',
      icon: 'ri-home-office-line',
      permits: [
        'Home Occupation Permit',
        'Zoning Compliance Verification',
        'HOA Approval (if applicable)',
        'Business License',
      ],
    },
  ];

  const zoningInfo = [
    {
      zone: 'Commercial (C-1, C-2, C-3)',
      description: 'Retail stores, offices, restaurants, and service businesses',
      restrictions: 'Limited hours of operation in some areas, parking requirements',
    },
    {
      zone: 'Industrial (I-1, I-2)',
      description: 'Manufacturing, warehousing, distribution centers',
      restrictions: 'Environmental compliance, noise restrictions, truck access requirements',
    },
    {
      zone: 'Mixed-Use',
      description: 'Combination of residential and commercial activities',
      restrictions: 'Specific use restrictions, design standards, parking ratios',
    },
    {
      zone: 'Residential (R-1, R-2, R-3)',
      description: 'Limited home-based businesses only',
      restrictions: 'No customer traffic, no signage, no employees (in most cases)',
    },
  ];

  const steps = [
    {
      number: '1',
      title: 'Verify Zoning Compliance',
      description: 'Contact El Paso Planning & Inspections to confirm your business type is allowed at your chosen location.',
      contact: '(915) 212-1553',
    },
    {
      number: '2',
      title: 'Obtain Federal & State IDs',
      description: 'Apply for EIN (IRS) and register with Texas Comptroller for state tax permits.',
      contact: 'IRS.gov & Comptroller.Texas.gov',
    },
    {
      number: '3',
      title: 'Apply for City Business License',
      description: 'Submit application to City of El Paso Tax Office with required documentation.',
      contact: '(915) 212-1040',
    },
    {
      number: '4',
      title: 'Schedule Required Inspections',
      description: 'Arrange health, fire, and building inspections as needed for your business type.',
      contact: 'Varies by department',
    },
    {
      number: '5',
      title: 'Obtain Industry-Specific Licenses',
      description: 'Apply for professional licenses or specialized permits required for your industry.',
      contact: 'Varies by industry',
    },
    {
      number: '6',
      title: 'Display Permits & Licenses',
      description: 'Post all required permits and licenses visibly at your business location.',
      contact: 'N/A',
    },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Permit & License Checklist",
        "item": "https://elpasotexasbusiness.com/resources/permit-license-checklist"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="El Paso Business Permit & License Checklist"
        description="Complete checklist of permits and licenses required to start a business in El Paso, Texas. Includes city, county, state, and federal requirements with application links and timelines."
        canonical="https://elpasotexasbusiness.com/resources/permit-license-checklist"
        ogImage="https://public.readdy.ai/ai/img_res/4d3c43ffbc7db3cbff031aae75e0521f.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold text-sm flex items-center space-x-2 whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Permit & License Checklist</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Navigate El Paso's permitting process with confidence. This comprehensive checklist covers all the permits and licenses you'll need to operate legally in El Paso, Texas.
              </p>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">General Requirements (All Businesses)</h2>
              <div className="space-y-6">
                {generalRequirements.map((item, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-sm text-[#D2691E] font-semibold mb-3">{item.authority}</p>
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">{item.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-money-dollar-circle-line text-xl text-white"></i>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Cost</p>
                          <p className="text-sm font-semibold text-gray-900">{item.cost}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-time-line text-xl text-white"></i>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Processing Time</p>
                          <p className="text-sm font-semibold text-gray-900">{item.timeline}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Industry-Specific Permits</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {industrySpecific.map((industry, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${industry.icon} text-2xl text-white`}></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{industry.category}</h3>
                    </div>
                    <ul className="space-y-3">
                      {industry.permits.map((permit, permitIndex) => (
                        <li key={permitIndex} className="flex items-start space-x-3">
                          <i className="ri-checkbox-circle-line text-[#D2691E] mt-0.5 flex-shrink-0"></i>
                          <span className="text-sm text-gray-700">{permit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Zoning Requirements</h2>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                El Paso's zoning regulations determine what types of businesses can operate in specific areas. Verify your location's zoning before signing a lease or purchasing property.
              </p>
              <div className="space-y-6">
                {zoningInfo.map((zone, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{zone.zone}</h3>
                    <p className="text-sm text-gray-700 mb-4"><strong>Allowed Uses:</strong> {zone.description}</p>
                    <p className="text-sm text-gray-600"><strong>Common Restrictions:</strong> {zone.restrictions}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Step-by-Step Application Process</h2>
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 flex items-start space-x-6">
                    <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-2">{step.description}</p>
                      <p className="text-sm text-[#D2691E] font-semibold">{step.contact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#1B3A3A] rounded-3xl p-12">
              <div className="max-w-3xl mx-auto text-center">
                <h3 className="text-4xl font-bold text-white mb-4">Important Contacts</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mt-8">
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-white mb-2">City of El Paso Tax Office</h4>
                    <p className="text-sm text-white/80 mb-2">Business licenses and permits</p>
                    <p className="text-sm text-white font-semibold">(915) 212-1040</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-white mb-2">Planning & Inspections</h4>
                    <p className="text-sm text-white/80 mb-2">Zoning and building permits</p>
                    <p className="text-sm text-white font-semibold">(915) 212-1553</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-white mb-2">Health Department</h4>
                    <p className="text-sm text-white/80 mb-2">Food service and health permits</p>
                    <p className="text-sm text-white font-semibold">(915) 212-6520</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-white mb-2">Fire Department</h4>
                    <p className="text-sm text-white/80 mb-2">Fire safety inspections</p>
                    <p className="text-sm text-white font-semibold">(915) 212-5600</p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg mt-8 whitespace-nowrap"
                >
                  <span>Need Help Navigating Permits?</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}